import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Check, 
  X, 
  Lightning, 
  ShieldCheck, 
  Headset,
  SpinnerGap,
  HardHat,
  Buildings,
  Star,
  Rocket,
  Crown,
  Users,
  Briefcase,
  ChartLineUp,
  ChatCircle,
  Bell,
  MagnifyingGlass,
  Certificate,
  Handshake,
  Clock,
  CurrencyDollar,
  Question,
  CaretDown,
  CaretUp,
  ArrowRight,
  Phone,
  Envelope,
  CheckCircle,
  Trophy,
  Target,
  Gear,
  FileText,
  CalendarBlank,
  Export,
  UsersThree,
  Eye,
  Sparkle
} from '@phosphor-icons/react';
import { paymentsAPI } from '../api/api';
import { toast } from '../hooks/use-toast';
import { Button } from '../components/ui/button';
import { useWorkerLang } from '../contexts/WorkerLanguageContext';
import { WORKER_PLANS, CONTRACTOR_PLANS, formatFeatureValue } from '../utils/planFeatures';
import { openCheckout } from '../utils/checkout';

// Detailed plan data - synced with planFeatures.js
const workerPlans = [
  {
    id: 'free_worker',
    name: WORKER_PLANS.free_worker.name,
    price: WORKER_PLANS.free_worker.price,
    period: 'month',
    tagline: 'Get started risk-free',
    description: 'Perfect for workers just starting out or testing the platform. Build your profile and start applying to jobs.',
    color: 'gray',
    icon: HardHat,
    features: {
      'Job Applications': `${WORKER_PLANS.free_worker.jobApplicationsLimit} per month`,
      'Platform Payout Fee': 'Standard',
      'Profile Visibility': formatFeatureValue(WORKER_PLANS.free_worker.searchVisibility),
      'Search Ranking': formatFeatureValue(WORKER_PLANS.free_worker.searchRanking),
      'Messaging': 'Basic (10/day)',
      'Job Alerts': formatFeatureValue(WORKER_PLANS.free_worker.jobAlerts),
      'Portfolio Photos': `${WORKER_PLANS.free_worker.portfolioPhotos} photos`,
      'Skills Listed': `${WORKER_PLANS.free_worker.skillsListed} skills`,
      'Support': formatFeatureValue(WORKER_PLANS.free_worker.support),
      'Profile Analytics': WORKER_PLANS.free_worker.profileAnalytics,
      'Verified Badge': WORKER_PLANS.free_worker.verifiedBadge,
      'Featured Profile': WORKER_PLANS.free_worker.featuredProfile,
      'Priority Support': WORKER_PLANS.free_worker.prioritySupport,
      'Profile Boost': WORKER_PLANS.free_worker.profileBoost,
      'Contract Templates': WORKER_PLANS.free_worker.contractTemplates === 0 ? false : `${WORKER_PLANS.free_worker.contractTemplates} templates`,
      'Invoice Generator': formatFeatureValue(WORKER_PLANS.free_worker.invoiceGenerator),
      'Time Tracking': formatFeatureValue(WORKER_PLANS.free_worker.timeTracking),
    },
    bestFor: ['New workers', 'Part-time freelancers', 'Testing the platform'],
    limitations: ['Limited applications', 'Basic visibility', 'No priority features'],
  },
  {
    id: 'pro_worker',
    name: WORKER_PLANS.pro_worker.name,
    price: WORKER_PLANS.pro_worker.price,
    period: 'month',
    tagline: 'Most popular for professionals',
    description: 'Ideal for active professionals looking to grow their client base and stand out from the competition.',
    color: 'blue',
    popular: true,
    icon: Rocket,
    features: {
      'Job Applications': 'Unlimited',
      'Platform Payout Fee': 'Standard',
      'Profile Visibility': formatFeatureValue(WORKER_PLANS.pro_worker.searchVisibility),
      'Search Ranking': formatFeatureValue(WORKER_PLANS.pro_worker.searchRanking),
      'Messaging': formatFeatureValue(WORKER_PLANS.pro_worker.messaging),
      'Job Alerts': formatFeatureValue(WORKER_PLANS.pro_worker.jobAlerts),
      'Portfolio Photos': 'Unlimited',
      'Skills Listed': 'Unlimited',
      'Support': formatFeatureValue(WORKER_PLANS.pro_worker.support),
      'Profile Analytics': WORKER_PLANS.pro_worker.profileAnalytics,
      'Verified Badge': WORKER_PLANS.pro_worker.verifiedBadge,
      'Featured Profile': WORKER_PLANS.pro_worker.featuredProfile,
      'Priority Support': WORKER_PLANS.pro_worker.prioritySupport,
      'Profile Boost': `${WORKER_PLANS.pro_worker.profileBoost}x per week` > 0 ? `${WORKER_PLANS.pro_worker.profileBoost}x per week` : false,
      'Contract Templates': WORKER_PLANS.pro_worker.contractTemplates > 0 ? `${WORKER_PLANS.pro_worker.contractTemplates} templates` : false,
      'Invoice Generator': formatFeatureValue(WORKER_PLANS.pro_worker.invoiceGenerator),
      'Time Tracking': formatFeatureValue(WORKER_PLANS.pro_worker.timeTracking),
    },
    bestFor: ['Full-time contractors', 'Growing professionals', 'Serious job seekers'],
    limitations: [],
  },
];

const contractorPlans = [
  {
    id: 'starter_contractor',
    name: CONTRACTOR_PLANS.starter_contractor.name,
    price: CONTRACTOR_PLANS.starter_contractor.price,
    period: 'month',
    tagline: 'Try before you commit',
    description: 'Perfect for small contractors or those testing the platform. Post jobs and find workers with basic features.',
    color: 'gray',
    icon: Buildings,
    commission: CONTRACTOR_PLANS.starter_contractor.commission,
    features: {
      'Job Postings': `${CONTRACTOR_PLANS.starter_contractor.jobPostingsLimit} per month`,
      'Worker Search': formatFeatureValue(CONTRACTOR_PLANS.starter_contractor.workerSearch),
      'Candidate Views': `${CONTRACTOR_PLANS.starter_contractor.candidateViews} per job`,
      'Messaging': 'Basic (20/day)',
      'Job Visibility': formatFeatureValue(CONTRACTOR_PLANS.starter_contractor.jobVisibility),
      'Team Members': `${CONTRACTOR_PLANS.starter_contractor.teamMembers} user`,
      'Applicant Tracking': formatFeatureValue(CONTRACTOR_PLANS.starter_contractor.applicantTracking),
      'Support': formatFeatureValue(CONTRACTOR_PLANS.starter_contractor.support),
      'Featured Jobs': CONTRACTOR_PLANS.starter_contractor.featuredListings === 0 ? false : `${CONTRACTOR_PLANS.starter_contractor.featuredListings} per month`,
      'Worker Analytics': CONTRACTOR_PLANS.starter_contractor.workerAnalytics,
      'Bulk Messaging': CONTRACTOR_PLANS.starter_contractor.bulkMessaging,
      'Priority Support': CONTRACTOR_PLANS.starter_contractor.prioritySupport,
      'API Access': CONTRACTOR_PLANS.starter_contractor.apiAccess,
      'Custom Branding': CONTRACTOR_PLANS.starter_contractor.customBranding,
      'Escrow Protection': formatFeatureValue(CONTRACTOR_PLANS.starter_contractor.escrowProtection),
      'Project Templates': CONTRACTOR_PLANS.starter_contractor.projectTemplates === 0 ? false : `${CONTRACTOR_PLANS.starter_contractor.projectTemplates} templates`,
    },
    bestFor: ['Solo contractors', 'Occasional hiring', 'Testing the platform'],
    limitations: ['Limited job posts', 'Basic features only', 'No featured listings'],
  },
  {
    id: 'business_contractor',
    name: CONTRACTOR_PLANS.business_contractor.name,
    price: CONTRACTOR_PLANS.business_contractor.price,
    period: 'month',
    tagline: 'Perfect for growing companies',
    description: 'Ideal for established contractors who hire regularly. Get more visibility, better tools, and priority support.',
    color: 'blue',
    popular: true,
    icon: ChartLineUp,
    commission: CONTRACTOR_PLANS.business_contractor.commission,
    features: {
      'Job Postings': 'Unlimited',
      'Worker Search': 'Advanced + AI matching',
      'Candidate Views': 'Unlimited',
      'Messaging': formatFeatureValue(CONTRACTOR_PLANS.business_contractor.messaging),
      'Job Visibility': formatFeatureValue(CONTRACTOR_PLANS.business_contractor.jobVisibility),
      'Team Members': 'Unlimited',
      'Applicant Tracking': 'Full ATS',
      'Support': formatFeatureValue(CONTRACTOR_PLANS.business_contractor.support),
      'Featured Jobs': 'Unlimited',
      'Worker Analytics': CONTRACTOR_PLANS.business_contractor.workerAnalytics,
      'Bulk Messaging': CONTRACTOR_PLANS.business_contractor.bulkMessaging,
      'Priority Support': CONTRACTOR_PLANS.business_contractor.prioritySupport,
      'API Access': CONTRACTOR_PLANS.business_contractor.apiAccess,
      'Custom Branding': CONTRACTOR_PLANS.business_contractor.customBranding,
      'Escrow Protection': formatFeatureValue(CONTRACTOR_PLANS.business_contractor.escrowProtection),
      'Project Templates': 'Unlimited',
    },
    bestFor: ['Growing companies', 'Regular hiring needs', 'Multiple projects'],
    limitations: [],
  },
];

// Feature comparison categories
const workerFeatureCategories = [
  {
    name: 'Job Search & Applications',
    features: ['Job Applications', 'Job Alerts', 'Search Ranking']
  },
  {
    name: 'Profile & Visibility',
    features: ['Profile Visibility', 'Portfolio Photos', 'Skills Listed', 'Verified Badge', 'Featured Profile', 'Profile Boost']
  },
  {
    name: 'Communication',
    features: ['Messaging', 'Support', 'Priority Support']
  },
  {
    name: 'Earnings & Payouts',
    features: ['Platform Payout Fee']
  },
  {
    name: 'Tools & Analytics',
    features: ['Profile Analytics', 'Contract Templates', 'Invoice Generator', 'Time Tracking']
  }
];

const contractorFeatureCategories = [
  {
    name: 'Job Posting & Visibility',
    features: ['Job Postings', 'Job Visibility', 'Featured Jobs']
  },
  {
    name: 'Worker Search & Hiring',
    features: ['Worker Search', 'Candidate Views', 'Applicant Tracking', 'Worker Analytics']
  },
  {
    name: 'Communication & Team',
    features: ['Messaging', 'Bulk Messaging', 'Team Members', 'Support', 'Priority Support']
  },
  {
    name: 'Advanced Features',
    features: ['API Access', 'Custom Branding', 'Escrow Protection', 'Project Templates']
  }
];

// FAQs
const faqs = [
  {
    question: 'Can I switch plans at any time?',
    answer: 'Yes! You can upgrade or downgrade your plan at any time. When upgrading, you\'ll get immediate access to new features. When downgrading, your current plan benefits remain until the end of your billing cycle.'
  },
  {
    question: 'What payment methods do you accept?',
    answer: 'We accept all major credit cards (Visa, MasterCard, American Express) and debit cards. All payments are processed securely through Stripe.'
  },
  {
    question: 'Is there a free trial for paid plans?',
    answer: 'Yes! All paid plans come with a 14-day free trial. You can cancel anytime during the trial period without being charged.'
  },
  {
    question: 'How does the commission structure work?',
    answer: 'There is none — ConstructHelp charges 0% commission on every plan. Escrow payouts release 100% of the project amount to you, and bank withdrawals are free. Your subscription is a flat monthly fee that unlocks features; we never take a percentage of your project payments.'
  },
  {
    question: 'Can I cancel my subscription?',
    answer: 'Absolutely. You can cancel your subscription at any time from your account settings. You\'ll retain access to paid features until the end of your current billing period.'
  },
  {
    question: 'Do you offer refunds?',
    answer: 'We offer a 30-day money-back guarantee for all new subscriptions. If you\'re not satisfied, contact our support team for a full refund.'
  },
  {
    question: 'What happens to my data if I downgrade?',
    answer: 'Your data is always safe. When downgrading, you keep access to your existing data, but new uploads may be limited based on your plan. Nothing is deleted automatically.'
  },
  {
    question: 'Do you offer discounts for annual billing?',
    answer: 'Yes! Choose annual billing to pay 12 months upfront and save 20% compared to monthly. Contact our sales team for custom enterprise pricing.'
  }
];

// Spanish translation lookup for pricing display strings
const PRICING_ES = {
  'Get started risk-free': 'Empieza sin riesgo',
  'Most popular for professionals': 'El más popular entre profesionales',
  'Maximum visibility & earnings': 'Máxima visibilidad y ganancias',
  'Try before you commit': 'Prueba antes de comprometerte',
  'Perfect for growing companies': 'Perfecto para empresas en crecimiento',
  'Complete solution for large firms': 'Solución completa para grandes empresas',
  'Perfect for workers just starting out or testing the platform. Build your profile and start applying to jobs.': 'Perfecto para trabajadores que recién empiezan o que prueban la plataforma. Crea tu perfil y empieza a postularte a trabajos.',
  'Ideal for active professionals looking to grow their client base and stand out from the competition.': 'Ideal para profesionales activos que buscan hacer crecer su cartera de clientes y destacarse de la competencia.',
  'For top professionals who want maximum exposure, premium features, and dedicated support to grow their business.': 'Para profesionales de primer nivel que desean máxima exposición, funciones premium y soporte dedicado para hacer crecer su negocio.',
  'Perfect for small contractors or those testing the platform. Post jobs and find workers with basic features.': 'Perfecto para contratistas pequeños o quienes prueban la plataforma. Publica trabajos y encuentra trabajadores con funciones básicas.',
  'Ideal for established contractors who hire regularly. Get more visibility, better tools, and priority support.': 'Ideal para contratistas establecidos que contratan con frecuencia. Obtén más visibilidad, mejores herramientas y soporte prioritario.',
  'Full-featured solution for large construction firms. Custom integrations, dedicated support, and every feature unlocked.': 'Solución con todas las funciones para grandes empresas constructoras. Integraciones personalizadas, soporte dedicado y todas las funciones desbloqueadas.',
  'New workers': 'Trabajadores nuevos',
  'Part-time freelancers': 'Freelancers de medio tiempo',
  'Testing the platform': 'Probar la plataforma',
  'Full-time contractors': 'Contratistas de tiempo completo',
  'Growing professionals': 'Profesionales en crecimiento',
  'Serious job seekers': 'Buscadores serios de trabajo',
  'Top-tier professionals': 'Profesionales de primer nivel',
  'Business owners': 'Dueños de negocio',
  'High-volume contractors': 'Contratistas de alto volumen',
  'Solo contractors': 'Contratistas independientes',
  'Occasional hiring': 'Contratación ocasional',
  'Growing companies': 'Empresas en crecimiento',
  'Regular hiring needs': 'Necesidades de contratación regular',
  'Multiple projects': 'Múltiples proyectos',
  'Large construction firms': 'Grandes empresas constructoras',
  'Multi-location operations': 'Operaciones en varias ubicaciones',
  'Enterprise needs': 'Necesidades empresariales',
  'Job Search & Applications': 'Búsqueda y Postulaciones',
  'Profile & Visibility': 'Perfil y Visibilidad',
  'Communication': 'Comunicación',
  'Earnings & Payouts': 'Ganancias y Pagos',
  'Tools & Analytics': 'Herramientas y Analíticas',
  'Job Posting & Visibility': 'Publicación y Visibilidad',
  'Worker Search & Hiring': 'Búsqueda y Contratación',
  'Communication & Team': 'Comunicación y Equipo',
  'Advanced Features': 'Funciones Avanzadas',
  'Job Applications': 'Postulaciones',
  'Platform Payout Fee': 'Comisión de Pago',
  'Profile Visibility': 'Visibilidad del Perfil',
  'Search Ranking': 'Posición en Búsqueda',
  'Messaging': 'Mensajería',
  'Job Alerts': 'Alertas de Trabajo',
  'Portfolio Photos': 'Fotos de Portafolio',
  'Skills Listed': 'Habilidades Listadas',
  'Support': 'Soporte',
  'Profile Analytics': 'Analíticas del Perfil',
  'Verified Badge': 'Insignia Verificada',
  'Featured Profile': 'Perfil Destacado',
  'Priority Support': 'Soporte Prioritario',
  'Profile Boost': 'Impulso de Perfil',
  'Contract Templates': 'Plantillas de Contrato',
  'Invoice Generator': 'Generador de Facturas',
  'Time Tracking': 'Control de Tiempo',
  'Job Postings': 'Publicaciones de Trabajo',
  'Worker Search': 'Búsqueda de Trabajadores',
  'Candidate Views': 'Vistas de Candidatos',
  'Job Visibility': 'Visibilidad del Trabajo',
  'Team Members': 'Miembros del Equipo',
  'Applicant Tracking': 'Seguimiento de Postulantes',
  'Featured Jobs': 'Trabajos Destacados',
  'Worker Analytics': 'Analíticas de Trabajadores',
  'Bulk Messaging': 'Mensajería Masiva',
  'API Access': 'Acceso a API',
  'Custom Branding': 'Marca Personalizada',
  'Escrow Protection': 'Protección de Depósito',
  'Project Templates': 'Plantillas de Proyecto',
  'Standard': 'Estándar',
  'Unlimited': 'Ilimitado',
  'Unlimited + Priority': 'Ilimitado + Prioridad',
  '0% — waived': '0% — exenta',
  'Basic (10/day)': 'Básica (10/día)',
  'Basic (20/day)': 'Básica (20/día)',
  'Full ATS': 'ATS Completo',
  'Full ATS + Custom': 'ATS Completo + Personalizado',
  'Advanced + AI matching': 'Avanzada + emparejamiento con IA',
  '24/7 Dedicated': '24/7 Dedicado',
  'Can I switch plans at any time?': '¿Puedo cambiar de plan en cualquier momento?',
  'Yes! You can upgrade or downgrade your plan at any time. When upgrading, you\'ll get immediate access to new features. When downgrading, your current plan benefits remain until the end of your billing cycle.': '¡Sí! Puedes subir o bajar de plan en cualquier momento. Al subir, obtienes acceso inmediato a las nuevas funciones. Al bajar, los beneficios de tu plan actual continúan hasta el final de tu ciclo de facturación.',
  'What payment methods do you accept?': '¿Qué métodos de pago aceptan?',
  'We accept all major credit cards (Visa, MasterCard, American Express) and debit cards. All payments are processed securely through Stripe.': 'Aceptamos todas las principales tarjetas de crédito (Visa, MasterCard, American Express), tarjetas de débito y transferencias bancarias para planes Enterprise. Todos los pagos se procesan de forma segura a través de Stripe.',
  'Is there a free trial for paid plans?': '¿Hay una prueba gratuita para los planes de pago?',
  'Yes! All paid plans come with a 14-day free trial. You can cancel anytime during the trial period without being charged.': '¡Sí! Todos los planes de pago incluyen una prueba gratuita de 14 días. Puedes cancelar en cualquier momento durante el periodo de prueba sin ningún cargo.',
  'How does the commission structure work?': '¿Cómo funciona la estructura de comisiones?',
  'There is none — ConstructHelp charges 0% commission on every plan. Escrow payouts release 100% of the project amount to you, and bank withdrawals are free. Your subscription is a flat monthly fee that unlocks features; we never take a percentage of your project payments.': 'No hay ninguna — ConstructHelp cobra 0% de comisión en todos los planes. Los pagos en garantía te liberan el 100% del monto del proyecto y los retiros bancarios son gratis. Tu suscripción es una tarifa mensual fija que desbloquea funciones; nunca tomamos un porcentaje de los pagos de tus proyectos.',
  'Can I cancel my subscription?': '¿Puedo cancelar mi suscripción?',
  'Absolutely. You can cancel your subscription at any time from your account settings. You\'ll retain access to paid features until the end of your current billing period.': 'Por supuesto. Puedes cancelar tu suscripción en cualquier momento desde la configuración de tu cuenta. Conservarás el acceso a las funciones de pago hasta el final de tu periodo de facturación actual.',
  'Do you offer refunds?': '¿Ofrecen reembolsos?',
  'We offer a 30-day money-back guarantee for all new subscriptions. If you\'re not satisfied, contact our support team for a full refund.': 'Ofrecemos una garantía de reembolso de 30 días para todas las nuevas suscripciones. Si no estás satisfecho, contacta a nuestro equipo de soporte para un reembolso completo.',
  'What happens to my data if I downgrade?': '¿Qué pasa con mis datos si bajo de plan?',
  'Your data is always safe. When downgrading, you keep access to your existing data, but new uploads may be limited based on your plan. Nothing is deleted automatically.': 'Tus datos siempre están seguros. Al bajar de plan, conservas el acceso a tus datos existentes, pero las nuevas cargas pueden estar limitadas según tu plan. Nada se elimina automáticamente.',
  'Do you offer discounts for annual billing?': '¿Ofrecen descuentos por facturación anual?',
  'Yes! Choose annual billing to pay 12 months upfront and save 20% compared to monthly. Contact our sales team for custom enterprise pricing.': '¡Sí! Elige la facturación anual para pagar 12 meses por adelantado y ahorrar 20% en comparación con la mensual. Contacta a nuestro equipo de ventas para precios empresariales personalizados.',
  // Competitor comparison
  'Commission on project payments': 'Comisión sobre pagos de proyectos',
  '0% — always': '0% — siempre',
  'Fees on some bookings': 'Cargos en algunas reservas',
  'Pay-per-lead instead': 'Pago por prospecto',
  'Forced to buy leads to get work': 'Obligado a comprar prospectos para conseguir trabajo',
  'No — quote requests come to you free': 'No — las solicitudes de cotización te llegan gratis',
  'Yes, pay-per-lead': 'Sí, pago por prospecto',
  'Yes, $15–$85+ per lead': 'Sí, $15–$85+ por prospecto',
  'Built-in escrow payment protection': 'Protección de pago en garantía integrada',
  'Free direct-to-bank payouts': 'Pagos directos al banco gratis',
  'E-sign contracts & milestone tracking': 'Contratos con firma electrónica y seguimiento de etapas',
  'Hire skilled workers & full crews': 'Contrata trabajadores calificados y cuadrillas completas',
  // Trust cards
  'Secure Payments': 'Pagos Seguros',
  "All transactions protected by Stripe's bank-level security": 'Todas las transacciones protegidas con la seguridad de nivel bancario de Stripe',
  'Cancel Anytime': 'Cancela Cuando Quieras',
  'No long-term contracts. Downgrade or cancel with one click': 'Sin contratos a largo plazo. Baja de plan o cancela con un clic',
  '24/7 Support': 'Soporte 24/7',
  'Real humans ready to help whenever you need assistance': 'Personas reales listas para ayudarte cuando lo necesites',
  '30-Day Guarantee': 'Garantía de 30 Días',
  "Full refund if you're not satisfied within 30 days": 'Reembolso completo si no estás satisfecho dentro de los 30 días',
  // Enterprise
  'Dedicated account manager': 'Gerente de cuenta dedicado',
  'Custom API integrations': 'Integraciones de API personalizadas',
  'White-label options': 'Opciones de marca blanca',
  'Volume-based discounts': 'Descuentos por volumen',
  'Custom training & onboarding': 'Capacitación e incorporación personalizadas',
  'SLA guarantees': 'Garantías de SLA',
  'Team Size': 'Tamaño del Equipo',
  'Commission': 'Comisión',
  'Dedicated Manager': 'Gerente Dedicado',
  'Full Access': 'Acceso Completo',
  'Custom pricing': 'Precio personalizado',
  'Enterprise Plan': 'Plan Enterprise',
  'month': 'mes',
  'Best Value': 'Mejor Valor',
};


const PricingPage = () => {
  const navigate = useNavigate();
  const { lang } = useWorkerLang();
  const L = (en, es) => (lang === 'es' ? es : en);
  const tr = (s) => (lang === 'es' ? (PRICING_ES[s] || s) : s);
  const trVal = (v) => {
    if (typeof v !== 'string' || lang !== 'es') return v;
    if (PRICING_ES[v]) return PRICING_ES[v];
    return v
      .replace(/per month/g, 'al mes')
      .replace(/per job/g, 'por trabajo')
      .replace(/per week/g, 'por semana')
      .replace(/photos/g, 'fotos')
      .replace(/skills/g, 'habilidades')
      .replace(/templates/g, 'plantillas')
      .replace(/\busers\b/g, 'usuarios')
      .replace(/\buser\b/g, 'usuario')
      .replace(/Unlimited/g, 'Ilimitado')
      .replace(/Standard/g, 'Estándar')
      .replace(/Priority/g, 'Prioridad')
      .replace(/waived/g, 'exenta');
  };
  const [userType, setUserType] = useState('worker');
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [showComparison, setShowComparison] = useState(false);

  const currentPlans = userType === 'worker' ? workerPlans : contractorPlans;
  const featureCategories = userType === 'worker' ? workerFeatureCategories : contractorFeatureCategories;

  const getPrice = (plan) => {
    if (plan.price === 0) return '$0';
    const price = billingPeriod === 'annual' ? plan.price * 0.8 : plan.price;
    return `$${price.toFixed(price % 1 === 0 ? 0 : 2)}`;
  };

  const handleStartTrial = async (planId, planName) => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast({ title: 'Login Required', description: 'Please login to start your trial', variant: 'destructive' });
      navigate('/login');
      return;
    }
    setLoadingPlan(planId);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/subscription/start-trial`,
        { plan_id: planId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: '14-day free trial started!', description: res.data?.message });
      navigate('/account/subscription');
    } catch (err) {
      toast({
        title: 'Could not start trial',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally { setLoadingPlan(null); }
  };

  const handleSubscribe = async (planId, planName, price) => {
    const token = localStorage.getItem('token');
    
    if (!token) {
      toast({
        title: 'Login Required',
        description: 'Please login to subscribe to a plan',
        variant: 'destructive'
      });
      navigate('/login');
      return;
    }

    if (price === 0) {
      toast({
        title: 'Free Plan Active',
        description: 'You are now on the free plan!',
      });
      return;
    }

    setLoadingPlan(planId);
    
    try {
      const response = await paymentsAPI.createCheckout({
        product_id: planId,
        product_type: 'subscription',
        origin_url: window.location.origin,
        billing_period: billingPeriod
      });
      
      openCheckout(response.data.checkout_url);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to start checkout',
        variant: 'destructive'
      });
    } finally {
      setLoadingPlan(null);
    }
  };

  const renderFeatureValue = (value) => {
    if (value === true) {
      return <Check size={20} weight="bold" className="text-emerald-600" />;
    }
    if (value === false) {
      return <X size={20} weight="bold" className="text-neutral-300" />;
    }
    return <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">{value}</span>;
  };

  return (
    <div className="min-h-screen bg-[#F0F0F2] dark:bg-neutral-900" data-testid="pricing-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#2B4C6F] via-[#4A7DA6] to-[#3A6A8A] text-white py-20 border-b-4 border-black">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 mb-6 border-2 border-white/30">
            <Sparkle size={16} weight="fill" className="text-amber-300" />
            <span className="text-sm font-semibold">{L('14-DAY FREE TRIAL ON ALL PAID PLANS', 'PRUEBA GRATIS DE 14 DÍAS EN TODOS LOS PLANES DE PAGO')}</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-black mb-4">
            {L('Plans & Pricing', 'Planes y Precios')}
          </h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto mb-8">
            {L('Choose the perfect plan for your needs. Transparent pricing, no hidden fees, cancel anytime.', 'Elige el plan perfecto para tus necesidades. Precios transparentes, sin cargos ocultos, cancela cuando quieras.')}
          </p>
          
          {/* User Type Toggle */}
          <div className="flex justify-center mb-6">
            <div className="bg-white/10 backdrop-blur-sm border-2 border-white/30 rounded-xl p-2">
              <div className="flex gap-2">
                <button
                  onClick={() => setUserType('worker')}
                  className={`flex items-center gap-2 px-6 py-3 rounded-lg font-bold transition-all ${
                    userType === 'worker'
                      ? 'bg-white text-[#2B4C6F]'
                      : 'text-white hover:bg-white/10'
                  }`}
                  data-testid="worker-tab"
                >
                  <HardHat size={24} weight="bold" />
                  {L('For Workers', 'Para Trabajadores')}
                </button>
                <button
                  onClick={() => setUserType('contractor')}
                  className={`flex items-center gap-2 px-6 py-3 rounded-lg font-bold transition-all ${
                    userType === 'contractor'
                      ? 'bg-white text-[#2B4C6F]'
                      : 'text-white hover:bg-white/10'
                  }`}
                  data-testid="contractor-tab"
                >
                  <Buildings size={24} weight="bold" />
                  {L('For Contractors', 'Para Contratistas')}
                </button>
              </div>
            </div>
          </div>

          {/* Billing Toggle (annual = 12 months upfront, 20% off) */}
          <div className="flex justify-center items-center gap-4">
            <span className={`font-bold ${billingPeriod === 'monthly' ? 'text-white' : 'text-blue-200'}`}>{L('Monthly', 'Mensual')}</span>
            <button
              onClick={() => setBillingPeriod(billingPeriod === 'monthly' ? 'annual' : 'monthly')}
              className={`relative w-16 h-8 rounded-full border-2 border-white/50 transition-colors ${
                billingPeriod === 'annual' ? 'bg-amber-400' : 'bg-white/20'
              }`}
              data-testid="billing-toggle"
            >
              <span className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-transform ${
                billingPeriod === 'annual' ? 'translate-x-8' : 'translate-x-1'
              }`} />
            </button>
            <span className={`font-bold ${billingPeriod === 'annual' ? 'text-white' : 'text-blue-200'}`}>
              {L('Annual', 'Anual')}
              <span className="ml-2 px-2 py-1 bg-amber-400 text-neutral-900 text-xs rounded-full">{L('Save 20%', 'Ahorra 20%')}</span>
            </span>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-16 -mt-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-6">
            {currentPlans.map((plan) => (
              <div
                key={plan.id}
                className={`relative bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl overflow-hidden shadow-[6px_6px_0px_#000] dark:shadow-[6px_6px_0px_#2B4C6F] ${
                  plan.popular ? 'ring-4 ring-[#2B4C6F]/30 scale-105 z-10' : ''
                }`}
                data-testid={`plan-card-${plan.id}`}
              >
                {/* Badge */}
                {(plan.popular || plan.badge) && (
                  <div className={`text-center py-2 font-bold text-sm ${
                    plan.popular ? 'bg-[#2B4C6F] text-white' : 'bg-amber-400 text-neutral-900'
                  }`}>
                    {plan.popular ? L('MOST POPULAR', 'MÁS POPULAR') : tr(plan.badge)}
                  </div>
                )}

                <div className="p-6">
                  {/* Plan Header */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center border-2 ${
                      plan.color === 'amber' ? 'bg-amber-100 border-amber-400' :
                      plan.color === 'blue' ? 'bg-blue-100 border-blue-400' :
                      'bg-neutral-100 border-neutral-300'
                    }`}>
                      <plan.icon size={24} weight="bold" className={
                        plan.color === 'amber' ? 'text-amber-600' :
                        plan.color === 'blue' ? 'text-blue-600' :
                        'text-neutral-600'
                      } />
                    </div>
                    <div>
                      <h3 className="text-2xl font-black text-neutral-900 dark:text-white">{plan.name}</h3>
                      <p className="text-sm text-neutral-500">{tr(plan.tagline)}</p>
                    </div>
                  </div>

                  {/* Price */}
                  <div className="mb-4">
                    <div className="flex items-baseline gap-1">
                      <span className="text-5xl font-black text-neutral-900 dark:text-white">{getPrice(plan)}</span>
                      <span className="text-neutral-500 font-bold">/{tr(plan.period)}</span>
                    </div>
                    {plan.commission !== undefined && (
                      <p className="text-sm text-emerald-600 font-bold mt-1">
                        {L('0% commission — keep 100% of project payments', '0% de comisión — quédate con el 100% de los pagos del proyecto')}
                      </p>
                    )}
                    {billingPeriod === 'annual' && plan.price > 0 && (
                      <p className="text-sm text-emerald-600 font-bold mt-1" data-testid={`annual-note-${plan.id}`}>
                        ${(plan.price * 12 * 0.8).toFixed(2)} {L('billed today · save', 'cobrado hoy · ahorra')} ${(plan.price * 12 * 0.2).toFixed(0)}{L('/year', '/año')}
                      </p>
                    )}
                  </div>

                  {/* Description */}
                  <p className="text-neutral-600 dark:text-neutral-400 text-sm mb-6">
                    {tr(plan.description)}
                  </p>

                  {/* Best For */}
                  <div className="mb-6">
                    <p className="text-xs font-bold text-neutral-500 uppercase mb-2">{L('Best For', 'Ideal Para')}</p>
                    <div className="flex flex-wrap gap-2">
                      {plan.bestFor.map((item, idx) => (
                        <span key={idx} className="px-2 py-1 bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300 text-xs rounded-full font-medium">
                          {tr(item)}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Key Features */}
                  <div className="space-y-3 mb-6">
                    {Object.entries(plan.features).slice(0, 6).map(([key, value]) => (
                      <div key={key} className="flex items-center justify-between">
                        <span className="text-sm text-neutral-600 dark:text-neutral-400">{tr(key)}</span>
                        {renderFeatureValue(typeof value === 'string' ? trVal(value) : value)}
                      </div>
                    ))}
                  </div>

                  {/* CTA Button */}
                  <button
                    onClick={() => plan.price === 0
                      ? handleSubscribe(plan.id, plan.name, plan.price)
                      : handleStartTrial(plan.id, plan.name)}
                    disabled={loadingPlan === plan.id}
                    className={`w-full h-14 flex items-center justify-center gap-2 font-bold text-lg rounded-xl border-2 border-black transition-all ${
                      plan.popular
                        ? 'bg-[#2B4C6F] text-white shadow-[4px_4px_0px_#000] hover:shadow-[5px_5px_0px_#000] active:translate-y-1 active:shadow-none'
                        : 'bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none'
                    } disabled:opacity-50`}
                    data-testid={`subscribe-${plan.id}`}
                  >
                    {loadingPlan === plan.id ? (
                      <SpinnerGap size={24} weight="bold" className="animate-spin" />
                    ) : (
                      <>
                        {plan.price === 0 ? L('Get Started Free', 'Empieza Gratis') : L('Start Free Trial', 'Iniciar Prueba Gratis')}
                        <ArrowRight size={20} weight="bold" />
                      </>
                    )}
                  </button>
                  {plan.price > 0 && (
                    <button
                      onClick={() => handleSubscribe(plan.id, plan.name, plan.price)}
                      disabled={loadingPlan === plan.id}
                      className="w-full mt-2 h-9 text-xs font-bold text-neutral-600 dark:text-neutral-300 hover:text-[#D95800] underline disabled:opacity-50"
                      data-testid={`subscribe-now-${plan.id}`}
                    >
                      {L('Skip trial — subscribe now', 'Omitir prueba — suscribirse ahora')}
                    </button>
                  )}

                  {plan.price > 0 && (
                    <p className="text-center text-xs text-neutral-500 mt-3">
                      {L('14-day free trial • Cancel anytime', 'Prueba gratis de 14 días • Cancela cuando quieras')}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Comparison Table */}
      <section className="py-16 bg-white dark:bg-neutral-800 border-y-4 border-black">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-black text-neutral-900 dark:text-white mb-4">
              {L('Compare All Features', 'Compara Todas las Funciones')}
            </h2>
            <p className="text-neutral-600 dark:text-neutral-400 max-w-2xl mx-auto">
              {L("See exactly what's included in each plan. Click to expand categories.", 'Ve exactamente qué incluye cada plan. Haz clic para expandir las categorías.')}
            </p>
            <button
              onClick={() => setShowComparison(!showComparison)}
              className="mt-4 px-6 py-3 bg-[#2B4C6F] text-white font-bold rounded-xl border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
              data-testid="toggle-comparison"
            >
              {showComparison ? L('Hide Comparison', 'Ocultar Comparación') : L('Show Full Comparison', 'Ver Comparación Completa')}
              {showComparison ? <CaretUp size={20} weight="bold" className="inline ml-2" /> : <CaretDown size={20} weight="bold" className="inline ml-2" />}
            </button>
          </div>

          {showComparison && (
            <div className="overflow-x-auto">
              <table className="w-full border-2 border-black rounded-xl overflow-hidden">
                <thead>
                  <tr className="bg-neutral-100 dark:bg-neutral-700">
                    <th className="text-left p-4 font-bold text-neutral-900 dark:text-white border-b-2 border-black">{L('Features', 'Funciones')}</th>
                    {currentPlans.map((plan) => (
                      <th key={plan.id} className={`p-4 text-center font-bold border-b-2 border-l-2 border-black ${
                        plan.popular ? 'bg-[#2B4C6F] text-white' : 'text-neutral-900 dark:text-white'
                      }`}>
                        {plan.name}
                        {plan.popular && <span className="block text-xs font-normal mt-1">{L('Most Popular', 'Más Popular')}</span>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {featureCategories.map((category, catIdx) => (
                    <React.Fragment key={category.name}>
                      <tr className="bg-neutral-50 dark:bg-neutral-800">
                        <td colSpan={4} className="p-3 font-bold text-[#2B4C6F] text-sm uppercase border-b border-neutral-200 dark:border-neutral-700">
                          {tr(category.name)}
                        </td>
                      </tr>
                      {category.features.map((feature, idx) => (
                        <tr key={feature} className={idx % 2 === 0 ? 'bg-white dark:bg-neutral-800' : 'bg-neutral-50 dark:bg-neutral-750'}>
                          <td className="p-4 text-neutral-700 dark:text-neutral-300 border-b border-neutral-200 dark:border-neutral-700">{tr(feature)}</td>
                          {currentPlans.map((plan) => (
                            <td key={plan.id} className={`p-4 text-center border-b border-l border-neutral-200 dark:border-neutral-700 ${
                              plan.popular ? 'bg-blue-50/50 dark:bg-blue-900/20' : ''
                            }`}>
                              {renderFeatureValue(typeof plan.features[feature] === 'string' ? trVal(plan.features[feature]) : plan.features[feature])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {/* 0% Commission */}
      {userType === 'contractor' && (
        <section className="py-16 bg-[#2B4C6F]">
          <div className="max-w-5xl mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
              {L('0% Commission. On Every Plan.', '0% de Comisión. En Todos los Planes.')}
            </h2>
            <p className="text-xl text-blue-100 mb-10">
              {L('We never take a cut of your project payments. Escrow releases pay out 100% — your subscription covers the tools, not a tax on your work.', 'Nunca tomamos una parte de los pagos de tus proyectos. Los depósitos en garantía liberan el 100% — tu suscripción cubre las herramientas, no un impuesto sobre tu trabajo.')}
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white/10 backdrop-blur-sm border-2 border-white/30 rounded-xl p-6">
                <p className="text-blue-100 font-bold mb-2">{L('Escrow Payouts', 'Pagos en Garantía')}</p>
                <p className="text-6xl font-black text-white mb-2">100%</p>
                <p className="text-blue-100 text-sm">{L('released to you, in full', 'liberados a ti, en su totalidad')}</p>
              </div>
              <div className="bg-white/20 backdrop-blur-sm border-2 border-white/50 rounded-xl p-6 scale-110 shadow-[4px_4px_0px_rgba(255,255,255,0.3)]">
                <p className="text-blue-100 font-bold mb-2">{L('Platform Commission', 'Comisión de Plataforma')}</p>
                <p className="text-6xl font-black text-white mb-2">0%</p>
                <p className="text-blue-100 text-sm">{L('on all plans, always', 'en todos los planes, siempre')}</p>
                <span className="inline-block mt-4 px-3 py-1 bg-amber-400 text-neutral-900 text-xs font-bold rounded-full">
                  {L('UNLIKE ANGI & THUMBTACK', 'A DIFERENCIA DE ANGI Y THUMBTACK')}
                </span>
              </div>
              <div className="bg-white/10 backdrop-blur-sm border-2 border-white/30 rounded-xl p-6">
                <p className="text-blue-100 font-bold mb-2">{L('Withdrawal Fees', 'Comisiones de Retiro')}</p>
                <p className="text-6xl font-black text-white mb-2">$0</p>
                <p className="text-blue-100 text-sm">{L('direct to your bank', 'directo a tu banco')}</p>
              </div>
            </div>
            <p className="mt-8 text-blue-100 text-sm">
              {L('Plans differ by features and limits — never by how much of your money we keep.', 'Los planes difieren por funciones y límites — nunca por cuánto de tu dinero nos quedamos.')}
            </p>
          </div>
        </section>
      )}

      {/* Competitor Comparison */}
      {userType === 'contractor' && (
        <section className="py-16 bg-neutral-900" data-testid="competitor-comparison">
          <div className="max-w-5xl mx-auto px-4">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-black text-white mb-3">
                {L('How We Stack Up', 'Cómo nos Comparamos')}
              </h2>
              <p className="text-lg text-neutral-400">
                {L("Other platforms charge you to find work, then take a cut of it. We don't.", 'Otras plataformas te cobran por encontrar trabajo y luego se quedan con una parte. Nosotros no.')}
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border-4 border-black bg-white dark:bg-neutral-800">
                <thead>
                  <tr>
                    <th className="p-4 text-left text-xs font-black uppercase tracking-widest text-neutral-500 bg-neutral-100 dark:bg-neutral-700 border-b-4 border-black"> </th>
                    <th className="p-4 text-center bg-[#D95800] border-b-4 border-l-2 border-black">
                      <span className="text-sm font-black uppercase tracking-wide text-white">ConstructHelp</span>
                    </th>
                    <th className="p-4 text-center bg-neutral-100 dark:bg-neutral-700 border-b-4 border-l-2 border-black">
                      <span className="text-sm font-black uppercase tracking-wide text-neutral-600 dark:text-neutral-300">Angi</span>
                    </th>
                    <th className="p-4 text-center bg-neutral-100 dark:bg-neutral-700 border-b-4 border-l-2 border-black">
                      <span className="text-sm font-black uppercase tracking-wide text-neutral-600 dark:text-neutral-300">Thumbtack</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    {
                      label: 'Commission on project payments',
                      us: '0% — always',
                      angi: 'Fees on some bookings',
                      tt: 'Pay-per-lead instead',
                      usGood: true,
                    },
                    {
                      label: 'Forced to buy leads to get work',
                      us: 'No — quote requests come to you free',
                      angi: 'Yes, pay-per-lead',
                      tt: 'Yes, $15–$85+ per lead',
                      usGood: true,
                    },
                    { label: 'Built-in escrow payment protection', us: true, angi: false, tt: false },
                    { label: 'Free direct-to-bank payouts', us: true, angi: '—', tt: '—' },
                    { label: 'E-sign contracts & milestone tracking', us: true, angi: false, tt: false },
                    { label: 'Hire skilled workers & full crews', us: true, angi: false, tt: false },
                  ].map((row, idx) => (
                    <tr key={row.label} className={idx % 2 === 0 ? 'bg-white dark:bg-neutral-800' : 'bg-neutral-50 dark:bg-neutral-750'}>
                      <td className="p-4 text-sm font-bold text-neutral-800 dark:text-neutral-200 border-t-2 border-neutral-200 dark:border-neutral-700">{tr(row.label)}</td>
                      <td className="p-4 text-center border-t-2 border-l-2 border-neutral-200 dark:border-neutral-700 bg-orange-50 dark:bg-orange-950/20">
                        {row.us === true
                          ? <Check size={20} weight="bold" className="inline text-emerald-600" />
                          : <span className="text-sm font-black text-[#D95800]">{tr(row.us)}</span>}
                      </td>
                      <td className="p-4 text-center border-t-2 border-l-2 border-neutral-200 dark:border-neutral-700">
                        {row.angi === false
                          ? <X size={18} weight="bold" className="inline text-neutral-400" />
                          : <span className="text-sm text-neutral-500 dark:text-neutral-400">{tr(row.angi)}</span>}
                      </td>
                      <td className="p-4 text-center border-t-2 border-l-2 border-neutral-200 dark:border-neutral-700">
                        {row.tt === false
                          ? <X size={18} weight="bold" className="inline text-neutral-400" />
                          : <span className="text-sm text-neutral-500 dark:text-neutral-400">{tr(row.tt)}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-center text-xs text-neutral-500">
              {L("Our optional leads marketplace lets you buy extra leads with credits — but it's never required to win work. Competitor details based on publicly available pricing information as of 2026 and may change. Angi and Thumbtack are trademarks of their respective owners.", 'Nuestro mercado opcional de prospectos te permite comprar prospectos adicionales con créditos — pero nunca es obligatorio para ganar trabajo. Los detalles de la competencia se basan en información de precios disponible públicamente a 2026 y pueden cambiar. Angi y Thumbtack son marcas registradas de sus respectivos propietarios.')}
            </p>
          </div>
        </section>
      )}

      {/* Trust & Security */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-black text-neutral-900 dark:text-white text-center mb-12">
            {L('Why Choose ConstructHelp?', '¿Por qué elegir ConstructHelp?')}
          </h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: ShieldCheck, title: 'Secure Payments', desc: "All transactions protected by Stripe's bank-level security", color: 'emerald' },
              { icon: Lightning, title: 'Cancel Anytime', desc: 'No long-term contracts. Downgrade or cancel with one click', color: 'amber' },
              { icon: Headset, title: '24/7 Support', desc: 'Real humans ready to help whenever you need assistance', color: 'blue' },
              { icon: CheckCircle, title: '30-Day Guarantee', desc: "Full refund if you're not satisfied within 30 days", color: 'purple' },
            ].map((item, idx) => (
              <div key={idx} className="bg-white dark:bg-neutral-800 border-2 border-black rounded-xl p-6 shadow-[4px_4px_0px_#000] text-center">
                <div className={`w-14 h-14 mx-auto mb-4 rounded-xl flex items-center justify-center border-2 ${
                  item.color === 'emerald' ? 'bg-emerald-100 border-emerald-400' :
                  item.color === 'amber' ? 'bg-amber-100 border-amber-400' :
                  item.color === 'blue' ? 'bg-blue-100 border-blue-400' :
                  'bg-purple-100 border-purple-400'
                }`}>
                  <item.icon size={28} weight="fill" className={
                    item.color === 'emerald' ? 'text-emerald-600' :
                    item.color === 'amber' ? 'text-amber-600' :
                    item.color === 'blue' ? 'text-blue-600' :
                    'text-purple-600'
                  } />
                </div>
                <h3 className="font-bold text-lg text-neutral-900 dark:text-white mb-2">{tr(item.title)}</h3>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">{tr(item.desc)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-white dark:bg-neutral-800 border-y-4 border-black">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-black text-neutral-900 dark:text-white text-center mb-4">
            {L('Frequently Asked Questions', 'Preguntas Frecuentes')}
          </h2>
          <p className="text-neutral-600 dark:text-neutral-400 text-center mb-12">
            {L("Got questions? We've got answers.", '¿Tienes preguntas? Tenemos respuestas.')}
          </p>
          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <div 
                key={idx}
                className="bg-neutral-50 dark:bg-neutral-700 border-2 border-black rounded-xl overflow-hidden shadow-[3px_3px_0px_#000]"
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between p-5 text-left font-bold text-neutral-900 dark:text-white hover:bg-neutral-100 dark:hover:bg-neutral-600 transition-colors"
                  data-testid={`faq-${idx}`}
                >
                  <span className="flex items-center gap-3">
                    <Question size={20} weight="bold" className="text-[#2B4C6F]" />
                    {tr(faq.question)}
                  </span>
                  {expandedFaq === idx ? (
                    <CaretUp size={20} weight="bold" />
                  ) : (
                    <CaretDown size={20} weight="bold" />
                  )}
                </button>
                {expandedFaq === idx && (
                  <div className="px-5 pb-5 pt-0 text-neutral-600 dark:text-neutral-300 border-t border-neutral-200 dark:border-neutral-600">
                    <p className="pt-4">{tr(faq.answer)}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enterprise CTA */}
      <section className="py-16 bg-gradient-to-br from-neutral-900 to-neutral-800 text-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-amber-400 text-neutral-900 rounded-full px-4 py-2 mb-6 font-bold text-sm">
                <Trophy size={16} weight="fill" />
                {L('ENTERPRISE SOLUTIONS', 'SOLUCIONES EMPRESARIALES')}
              </div>
              <h2 className="text-3xl md:text-4xl font-black mb-4">
                {L('Need a Custom Solution?', '¿Necesitas una Solución Personalizada?')}
              </h2>
              <p className="text-neutral-300 text-lg mb-6">
                {L('For large construction firms with unique requirements, we offer custom enterprise packages with dedicated support, API integrations, custom branding, and volume discounts.', 'Para grandes empresas constructoras con requisitos únicos, ofrecemos paquetes empresariales personalizados con soporte dedicado, integraciones de API, marca personalizada y descuentos por volumen.')}
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  'Dedicated account manager',
                  'Custom API integrations',
                  'White-label options',
                  'Volume-based discounts',
                  'Custom training & onboarding',
                  'SLA guarantees'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3">
                    <Check size={20} weight="bold" className="text-emerald-400" />
                    <span>{tr(item)}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-4">
                <Button
                  onClick={() => navigate('/contact')}
                  size="lg"
                  className="bg-[#2B4C6F] hover:bg-[#4A7DA6] text-white font-bold px-8 h-14 border-2 border-black shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none"
                  data-testid="contact-sales-btn"
                >
                  <Phone size={20} weight="bold" className="mr-2" />
                  {L('Contact Sales', 'Contactar Ventas')}
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="bg-transparent border-2 border-white text-white hover:bg-white/10 font-bold px-8 h-14"
                >
                  <Envelope size={20} weight="bold" className="mr-2" />
                  {L('Request Demo', 'Solicitar Demo')}
                </Button>
              </div>
            </div>
            <div className="hidden md:block">
              <div className="bg-neutral-800 border-2 border-neutral-700 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-amber-400 rounded-xl flex items-center justify-center">
                    <Buildings size={24} weight="bold" className="text-neutral-900" />
                  </div>
                  <div>
                    <p className="font-bold text-lg">{L('Enterprise Plan', 'Plan Enterprise')}</p>
                    <p className="text-neutral-400 text-sm">{L('Custom pricing', 'Precio personalizado')}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {[
                    { label: 'Team Size', value: 'Unlimited' },
                    { label: 'Commission', value: '0% — always' },
                    { label: 'Support', value: 'Dedicated Manager' },
                    { label: 'API Access', value: 'Full Access' },
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between py-2 border-b border-neutral-700">
                      <span className="text-neutral-400">{tr(item.label)}</span>
                      <span className="font-bold">{tr(item.value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-[#2B4C6F] text-white text-center">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-black mb-4">
            {L('Ready to Get Started?', '¿Listo para Empezar?')}
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            {L('Start growing your business with ConstructHelp today.', 'Empieza a hacer crecer tu negocio con ConstructHelp hoy.')}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              onClick={() => navigate('/signup')}
              size="lg"
              className="bg-white text-[#2B4C6F] hover:bg-blue-50 font-bold px-8 h-14 border-2 border-black shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none"
              data-testid="final-cta-btn"
            >
              {L('Start Free Trial', 'Iniciar Prueba Gratis')}
              <ArrowRight size={20} weight="bold" className="ml-2" />
            </Button>
          </div>
          <p className="mt-6 text-blue-100 text-sm">
            {L('No credit card required • 14-day free trial • Cancel anytime', 'Sin tarjeta de crédito • Prueba gratis de 14 días • Cancela cuando quieras')}
          </p>
        </div>
      </section>
    </div>
  );
};

export default PricingPage;
